/**
 * OptionsEngine — the shared rendering core for config-driven operator input forms.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * PURPOSE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * OptionsEngine renders a set of input fields (toggles, text, multiselect, etc.)
 * from a declarative config array. It handles form state, grouping, sub-grouping,
 * and cross-field dependencies internally. It exposes only `submit()` and `reset()`
 * to its parent via a ref.
 *
 * It is designed to be mounted inside a "shell" component that provides:
 *   - A container (Drawer, Dialog, inline div, etc.)
 *   - Header/footer chrome (title, buttons)
 *   - Output transformation (how to reshape the raw values for the consumer)
 *
 * OptionsEngine fills 100% of its parent's available space and renders no
 * container or buttons of its own.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE — WHY useImperativeHandle + ref?
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * OptionsEngine owns react-hook-form internally (encapsulation). The shell's
 * Submit/Reset buttons live outside this component. The shell needs a way to say
 * "submit now" or "reset now" — imperative actions that don't map to props/state.
 *
 * The ref pattern (useImperativeHandle) is the idiomatic React solution:
 *   - The shell calls `formRef.current.submit()` from its button's onClick
 *   - OptionsEngine runs validation, collects values, calls `onSubmit(values)`
 *   - The shell receives values via the callback and transforms them as needed
 *
 * This keeps form internals fully encapsulated. The shell never imports useForm,
 * never knows about field names, never manages validation state.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Example 1: Inside a Drawer shell (Filters)
 * ```jsx
 * const FiltersShell = ({ config, supportData, onApply }) => {
 *     const engineRef = useRef();
 *     return (
 *         <Drawer>
 *             <OptionsEngine ref={engineRef} config={config} supportData={supportData} onSubmit={onApply} />
 *             <Button onClick={() => engineRef.current.submit()}>Apply</Button>
 *             <Button onClick={() => engineRef.current.reset()}>Reset</Button>
 *         </Drawer>
 *     );
 * };
 * ```
 *
 * Example 2: Inside a Dialog shell (OptionsDialog)
 * ```jsx
 * const OptionsDialog = ({ config, onContinue, open }) => {
 *     const engineRef = useRef();
 *     return (
 *         <Dialog open={open}>
 *             <OptionsEngine ref={engineRef} config={config.items} onSubmit={onContinue} />
 *             <Button onClick={() => engineRef.current.reset()}>Clear</Button>
 *             <Button onClick={() => engineRef.current.submit()}>Continue</Button>
 *         </Dialog>
 *     );
 * };
 * ```
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * CONFIG SHAPE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * The `config` prop is an array of field descriptors:
 * ```js
 * [
 *     {
 *         Key: "fieldKey",                  // unique identifier (form field name)
 *         Label: "Display label",
 *         Type: "toggleInput" | "text" | "multiselect" | "sizeInput" | "dateInput",
 *         CategoryName: "Group A",          // optional — groups under Accordion
 *         SubCategoryName: "Extra options", // optional — gold divider within group
 *         Options: {
 *             defaultValue: ...,            // optional override
 *             EntrySource: ...,             // for multiselect (string key or inline array)
 *             EntryDisplay: "name",         // for multiselect — display property
 *             EntryValue: "id",             // for multiselect — value property
 *             EntryValidation: {
 *                 Dependency: "otherKey",
 *                 DependencyType: "disabled_when_falsy" | "in_larger_category" | "upper_limit" | "lower_limit",
 *                 LocalProperty: "...",     // for in_larger_category
 *                 DependencyProperty: "...",// for in_larger_category
 *             }
 *         }
 *     },
 * ]
 * ```
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * RENDERING RULES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * - Items with `CategoryName` → grouped under an Accordion with that name
 * - Items without `CategoryName` → rendered flat (no accordion)
 * - Items with same `SubCategoryName` within a group → rendered below a gold
 *   divider whose label is the SubCategoryName value
 * - `DependencyType: "disabled_when_falsy"` → field is always visible but
 *   greyed out when its dependency's value is falsy
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * REF API (imperative actions only)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * formRef.current.submit()      — validate + call onSubmit with values (or expand first error accordion on failure)
 * formRef.current.reset()       — reset all fields to defaults
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * REACTIVE STATE (via props)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * setIsValid — optional state setter called whenever form validity changes.
 *              Parent passes its own setState function; OptionsEngine calls it
 *              with true/false so the parent can disable/enable submit buttons.
 */
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import {useForm, useWatch} from "react-hook-form";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import {Accordion, AccordionDetails, AccordionSummary} from "../../utils/uiUtils";
import {OptionField, getFieldDefault} from "./OptionField";

/**
 * Internal wrapper: computes disabled state from "disabled_when_falsy" dependency
 * and passes it to OptionField.
 */
const DisabledAwareField = ({fieldConfig, control, ...rest}) => {
    const depKey = fieldConfig?.Options?.EntryValidation?.Dependency;
    const depType = fieldConfig?.Options?.EntryValidation?.DependencyType;

    const depValue = useWatch({
        control,
        name: depKey || "__noop__",
        defaultValue: undefined,
        disabled: !depKey || depType !== "disabled_when_falsy",
    });

    const isDisabled = depType === "disabled_when_falsy" && !depValue;

    return (
        <OptionField
            fieldConfig={fieldConfig}
            control={control}
            disabled={isDisabled}
            {...rest}
        />
    );
};

/**
 * Internal component: watches form values and evaluates computeCanProceed.
 * Renders null — isolated re-render scope, zero DOM cost.
 */
const CanProceedWatcher = ({control, computeCanProceed, setCanProceed}) => {
    const values = useWatch({control});
    useEffect(() => {
        setCanProceed?.(computeCanProceed(values));
    }, [values]);
    return null;
};

export const OptionsEngine = forwardRef(({config = [], supportData = {}, onSubmit, setIsValid, computeCanProceed, setCanProceed}, ref) => {

    // ─── Derive default values from config ────────────────────────────────────
    const getDefaultValues = useCallback(() => {
        const seenKeys = [];
        const defaults = {};
        config.forEach(item => {
            const key = item.Key;
            if (!key) return;
            if (seenKeys.includes(key)) throw new Error(`OptionsEngine: duplicate Key "${key}" in config`);
            seenKeys.push(key);
            const {defaultValue, sizeUnitValue} = getFieldDefault(item);
            defaults[key] = defaultValue;
            if (sizeUnitValue !== undefined) {
                defaults[`${key}_size_unit`] = sizeUnitValue;
            }
        });
        return defaults;
    }, [config]);

    // ─── Form state ───────────────────────────────────────────────────────────
    const {
        handleSubmit,
        control,
        getValues,
        reset,
        trigger,
        formState: {errors, isValid},
    } = useForm({
        defaultValues: getDefaultValues(),
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    // ─── Signal validity to parent ────────────────────────────────────────────
    useEffect(() => {
        setIsValid?.(isValid);
    }, [isValid]);

    // ─── Reset form when config changes ───────────────────────────────────────
    useEffect(() => {
        reset(getDefaultValues());
    }, [config]);

    // ─── Grouping logic ───────────────────────────────────────────────────────
    const {categoryNames, ungroupedItems, groupedItems} = useMemo(() => {
        const categories = [];
        const grouped = {};
        const ungrouped = [];

        config.forEach(item => {
            const cat = item.CategoryName;
            if (cat) {
                if (!categories.includes(cat)) categories.push(cat);
                (grouped[cat] ??= []).push(item);
            } else {
                ungrouped.push(item);
            }
        });

        return {categoryNames: categories, ungroupedItems: ungrouped, groupedItems: grouped};
    }, [config]);

    // ─── Accordion state ──────────────────────────────────────────────────────
    const [expanded, setExpanded] = useState('');

    const categoryHasErrors = (categoryName) => {
        const items = groupedItems[categoryName] || [];
        return items.some(item => errors[item.Key]);
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        if (!newExpanded && categoryHasErrors(panel)) return;
        setExpanded(newExpanded ? panel : false);
    };

    // ─── Nest flat form values by CategoryName ──────────────────────────────────
    const nestValues = (flatValues) => {
        const nested = {};
        config.forEach(item => {
            const key = item.Key;
            if (!key || !(key in flatValues)) return;
            const group = item.CategoryName || "_";
            (nested[group] ??= {})[key] = flatValues[key];
        });
        return nested;
    };

    // ─── Expose imperative actions to parent shell ────────────────────────────
    useImperativeHandle(ref, () => ({
        submit: () => handleSubmit(
            (values) => onSubmit?.(nestValues(values)),
            (formErrors) => {
                // On validation failure: expand first accordion with errors
                for (const categoryName of categoryNames) {
                    if ((groupedItems[categoryName] || []).some(item => formErrors[item.Key])) {
                        setExpanded(categoryName);
                        break;
                    }
                }
            }
        )(),
        reset: () => reset(getDefaultValues()),
    }));

    // ─── Lookup helper for cross-field dependencies ───────────────────────────
    const getOtherField = useCallback((key) => {
        return config.find(item => item.Key === key) || null;
    }, [config]);

    // ─── Render a list of fields, handling SubCategoryName dividers ───────────
    const renderFieldList = (items) => {
        const rootItems = items.filter(item => !item.SubCategoryName);
        const subGroups = {};
        items.filter(item => item.SubCategoryName).forEach(item => {
            (subGroups[item.SubCategoryName] ??= []).push(item);
        });

        return (
            <>
                {rootItems.map((item, idx) => (
                    <DisabledAwareField
                        key={`field-${item.Key}-${idx}`}
                        fieldConfig={item}
                        getOtherFieldFn={getOtherField}
                        control={control}
                        trigger={trigger}
                        errors={errors}
                        getValuesFn={getValues}
                        supportData={supportData}
                    />
                ))}
                {Object.entries(subGroups).map(([subCatName, subItems]) => (
                    <Grid item xs={12} key={`subcat-${subCatName}`}>
                        <Divider
                            sx={{
                                my: 1.5,
                                "&::before, &::after": {borderColor: "#FFC540"},
                            }}
                            textAlign="left"
                        >
                            <Typography sx={{
                                color: "#FFC540",
                                fontFamily: "Poppins, sans-serif",
                                fontSize: 11,
                            }}>
                                {subCatName}
                            </Typography>
                        </Divider>
                        {subItems.map((item, idx) => (
                            <DisabledAwareField
                                key={`field-${item.Key}-${idx}`}
                                fieldConfig={item}
                                getOtherFieldFn={getOtherField}
                                control={control}
                                trigger={trigger}
                                errors={errors}
                                getValuesFn={getValues}
                                supportData={supportData}
                            />
                        ))}
                    </Grid>
                ))}
            </>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 1, width: "100%"}}>
            {categoryNames.map((categoryName, catIdx) => (
                <Accordion
                    key={`accordion-${catIdx}-${categoryName}`}
                    expanded={expanded === categoryName}
                    onChange={handleAccordionChange(categoryName)}
                    TransitionProps={{unmountOnExit: false}}
                    sx={{boxSizing: "border-box", margin: "0 !important", "& .MuiCollapse-wrapper": {marginLeft: 0}}}
                >
                    <AccordionSummary
                        aria-controls={`${categoryName}-content`}
                        id={`${categoryName}-header`}
                    >
                        <Typography sx={{fontFamily: "Poppins", fontSize: 12}}>
                            {categoryName}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{borderRight: "none"}}>
                        <Grid container spacing={0} rowSpacing={2}>
                            {renderFieldList(groupedItems[categoryName])}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}

            {ungroupedItems.length > 0 && (
                <Grid container rowSpacing={1}>
                    {renderFieldList(ungroupedItems)}
                </Grid>
            )}

            {computeCanProceed && setCanProceed && (
                <CanProceedWatcher
                    control={control}
                    computeCanProceed={computeCanProceed}
                    setCanProceed={setCanProceed}
                />
            )}
        </Box>
    );
});
